import { Injectable } from '@angular/core';
import { ClassProvider } from './class';
import { BaseProvider } from './base';
import { BrainProvider, BrainDetails } from './brain';
import { BugsnagProvider } from './bugsnag';
import { NumberProvider } from './number';

import {
    BRAIN_ACCURACY_MESSAGES,
    BRAIN_ACCURACY_DESCRIPTORS,
    MIN_ASSETS_FOR_CLASS,
    HINT_TYPES
} from '../components/workspace/accuracy/constants';
import { TranslateService } from '@ngx-translate/core';

const { NOT_AVAILABLE, STILL_LEARNING, LOW, GOOD, GREAT } = BRAIN_ACCURACY_DESCRIPTORS;
const { PROBLEM_CLASSES, INSUFFICIENT_ASSETS } = HINT_TYPES;

/**
 * Wrapper function for the logic to generate each individual hint type
 * Ported from the accuracy drawer component (app\src\components\workspace\accuracy\index.ts)
 * At some point, we'll want to remove that component.
 */
@Injectable()
export class BrainScoreProvider extends BaseProvider {
    private hints: any[] = [];

    constructor(
        private classProvider: ClassProvider,
        private brainProvider: BrainProvider,
        private bugSnagProvider: BugsnagProvider,
        private numberProvider: NumberProvider,
        private translate: TranslateService
    ) {
        super();
    }

    public generateHintsForClasses(brain: BrainDetails): any[] {
        this.hints = [];
        this.generateProblemClassHint(brain);
        this.generateInsufficientAssetsHint(brain);
        return this.hints;
    }

    private getProblemClassList(descriptor, brainDetails: BrainDetails) {
        const classes = [...brainDetails.classes]
            .filter(({ accuracy, numValidationPolygons, numTrainedPolygons }) => {
                switch (descriptor) {
                    case STILL_LEARNING:
                        return numValidationPolygons + numTrainedPolygons < 10;
                    case LOW:
                        return this.accuracyToPercent(accuracy) < 70 || numValidationPolygons + numTrainedPolygons < 20;
                    case GOOD:
                    case GREAT:
                        return false;
                }
            })
            .sort((prev, next) => {
                return prev.accuracy - next.accuracy;
            })
            .slice(0, 3)
            .map(({ classIdentifier, accuracy }) => {
                const singleClass = this.classProvider.getClassByID(classIdentifier);
                if (singleClass) {
                    return {
                        name: singleClass.name,
                        value: this.accuracyToPercent(accuracy),
                        color: singleClass.color
                    };
                } else {
                    this.bugSnagProvider.logInfo(
                        `INFO: undefined class returned for class identifier ${classIdentifier}`
                    );
                }
            });
        // Remove any "undefined"/falsey classes (classes that have been deleted and are therefore dirty)
        return classes.filter(value => value);
    }

    private generateInsufficientAssetsHint(brainDetails: BrainDetails) {
        const { classes } = brainDetails;
        if (classes) {
            const insufficientClasses = [...classes].filter(
                ({ numValidationPolygons, numTrainedPolygons }) =>
                    numValidationPolygons + numTrainedPolygons < MIN_ASSETS_FOR_CLASS
            );
            if (insufficientClasses.length) {
                this.hints = [
                    ...this.hints,
                    {
                        hintType: INSUFFICIENT_ASSETS,
                        hintData: {
                            hintTitle: this.translate.instant('Not seeing some of the classes you trained?'),
                            hintMessage: this.translate.instant(
                                'We only calculate accuracy on a small percentage of images that are held back from being trained on (i.e. the validation set), so it may take at least 5-10 images for any given class to have an accuracy reading.'
                            )
                        }
                    }
                ];
            }
        }
    }

    private generateProblemClassHint(brainDetails: BrainDetails) {
        const accuracyDescriptor = this.brainProvider.getBrainScore(brainDetails);
        if (accuracyDescriptor != NOT_AVAILABLE) {
            let accuracyStatement = '';
            const problemClasses = this.getProblemClassList(accuracyDescriptor, brainDetails);
            for (const key in BRAIN_ACCURACY_DESCRIPTORS) {
                if (BRAIN_ACCURACY_DESCRIPTORS[key] == accuracyDescriptor) {
                    accuracyStatement = BRAIN_ACCURACY_MESSAGES[key];
                }
            }

            this.hints = [
                ...this.hints,
                {
                    hintType: PROBLEM_CLASSES,
                    hintData: {
                        accuracyDescriptor,
                        accuracyStatement,
                        problemClasses
                    }
                }
            ];
        }
    }

    private accuracyToPercent(value: number): number {
        return value <= 1 && value > 0 ? Math.round(value * 1000) / 10 : value;
    }

    public getSRAccuracy(truePositives: number, totalAssets: number): number | undefined | null {
        return this.numberProvider.round((truePositives / totalAssets) * 100, 2);
    }

    public getSRPrecision(truePositives: number, falsePositives: number): number | undefined | null {
        return truePositives + falsePositives > 0
            ? this.numberProvider.round((truePositives / (truePositives + falsePositives)) * 100, 2)
            : 0;
    }

    public getSRRecall(truePositives: number, falseNegatives: number): number | undefined | null {
        return truePositives + falseNegatives > 0
            ? this.numberProvider.round((truePositives / (truePositives + falseNegatives)) * 100, 2)
            : 0;
    }
}



// WEBPACK FOOTER //
// ./src/providers/brain_score.ts